<div class="justup-dialog">
  <vl-header-dialog (close)="dialogRef.close()" [rightElement]="excelBtn">
    {{ title() }}
  </vl-header-dialog>

  <ng-template #excelBtn>
    <vl-excel-export-button size="medium"
                            [loading]="exporting()"
                            [disabled]="exporting()"
                            [tooltip]="'dashboard_ui.export_excel' | trans"
                            (action)="exportExcel()"/>
  </ng-template>

  <!-- Sélecteur de pivot — reprend le toggle du widget pour pouvoir changer
       de dimension sans refermer la modal. -->
  <div class="flex-row justify-center p-8">
    <vl-chips-toggle [elements]="groupByElements" [(value)]="groupBy"/>
  </div>

  <div class="justup-dialog-content flex-col gap-16">
    <!-- Stats cards -->
    <div class="flex-row gap-8 wrap">
      <vl-radio-card mode="display" color="valid" icon="euro" [title]="'dashboard_ui.valeur_totale' | trans" style="flex: 1; min-width: 160px">
        <span content class="headline">{{ stats().totalFormatted }}</span>
      </vl-radio-card>

      <vl-radio-card mode="display" color="secondary" icon="inventory_2" [title]="stats().countLabel | titlecase" style="flex: 1; min-width: 160px">
        <div content class="flex-col gap-2">
          <span class="headline">{{ stats().count }}</span>
          <span class="tiny" style="opacity: 0.7">{{ 'dashboard_ui.moyenne' | trans }} {{ stats().avgFormatted }}</span>
        </div>
      </vl-radio-card>

      <vl-radio-card mode="display" color="warn" icon="leaderboard" [title]="'dashboard_ui.concentration' | trans" style="flex: 1; min-width: 160px">
        <div content class="flex-col gap-2">
          <div class="flex-row align-baseline gap-4">
            <span class="headline">{{ stats().pareto80Count }}</span>
            <span class="body-2" style="opacity: 0.7">/ {{ stats().count }}</span>
          </div>
          <span class="tiny" style="opacity: 0.7">{{ 'dashboard_ui.concentration_desc' | trans }}</span>
        </div>
      </vl-radio-card>
    </div>

    <!-- Detail tree -->
    @for (row of rows(); track row.denomination) {
      <!-- LEVEL 0 -->
      <div>
        <div class="flex-row gap-8 p-8 align-center border-box border-8 cursor-pointer"
             [style.border-left]="getColor(row.denomination) ? '3px solid ' + getColor(row.denomination) : '1px solid var(--color-outline)'"
             style="border: 1px solid var(--color-outline)"
             (click)="toggle(row.denomination)">
          <mat-icon class="on-disabled shrink-0" style="font-size: 16px; width: 16px; height: 16px">
            {{ isOpen(row.denomination) ? 'expand_more' : 'chevron_right' }}
          </mat-icon>
          @if (getColor(row.denomination)) {
            <span class="shrink-0 border-full" [style.background-color]="getColor(row.denomination)" style="width: 10px; height: 10px"></span>
          }
          <span class="grow caption on-background-alt" style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap">
            {{ row.denomination }}
          </span>
          <span class="tiny on-disabled shrink-0" style="min-width: 70px; text-align: right">{{ row.total | number:'1.0-0' }} €</span>
          <span class="tiny on-accent shrink-0" style="min-width: 45px; text-align: right">{{ row.percentage }}%</span>
        </div>

        @if (isOpen(row.denomination)) {
          <!-- Mode `byFamille` : on rend en parallèle :
               1) sous-familles (LEVEL 1) → articles (LEVEL 2) → lots
               2) articles orphelins sans sous-famille → directement sous la
                  famille (LEVEL 1 = article), pas de wrapper "Non classé"
               Modes hors byFamille : `subGroups` vide, seuls les articles
               rendus directement sous le groupe (3 niveaux). -->
          <div class="flex-col gap-1 mt-2 mb-2" style="margin-left: 24px">
            @for (sf of row.subGroups; track sf.denomination) {
              <div>
                <div class="flex-row gap-8 p-4 align-center cursor-pointer"
                     (click)="toggle(row.denomination + '::sf::' + sf.denomination); $event.stopPropagation()">
                  <mat-icon class="on-disabled shrink-0" style="font-size: 14px; width: 14px; height: 14px">
                    {{ isOpen(row.denomination + '::sf::' + sf.denomination) ? 'expand_more' : 'chevron_right' }}
                  </mat-icon>
                  <span class="grow caption on-background-alt" style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 500">
                    {{ sf.denomination }}
                  </span>
                  <span class="tiny on-disabled shrink-0" style="min-width: 70px; text-align: right">{{ sf.total | number:'1.0-0' }} €</span>
                  <span class="tiny on-accent shrink-0" style="min-width: 45px; text-align: right">{{ sf.percentage }}%</span>
                </div>

                @if (isOpen(row.denomination + '::sf::' + sf.denomination)) {
                  <div class="flex-col gap-1 mt-1 mb-1" style="margin-left: 24px">
                    @for (art of sf.articles; track art.denomination) {
                      <ng-container
                        *ngTemplateOutlet="articleTpl; context: { art, parentKey: row.denomination + '::sf::' + sf.denomination }"
                      ></ng-container>
                    }
                  </div>
                }
              </div>
            }
            @for (art of row.articles; track art.denomination) {
              <ng-container
                *ngTemplateOutlet="articleTpl; context: { art, parentKey: row.denomination }"
              ></ng-container>
            }
          </div>
        }
      </div>
    }

    <!-- Template article + lots, factorisé pour être appelé soit directement
         (modes hors byFamille) soit imbriqué dans une sous-famille (byFamille). -->
    <ng-template #articleTpl let-art="art" let-parentKey="parentKey">
      <div>
        <div class="flex-row gap-8 p-4 align-center cursor-pointer"
             (click)="toggle(parentKey + '::' + art.denomination); $event.stopPropagation()">
          <mat-icon class="on-disabled shrink-0" style="font-size: 14px; width: 14px; height: 14px">
            {{ isOpen(parentKey + '::' + art.denomination) ? 'expand_more' : 'chevron_right' }}
          </mat-icon>
          <span class="grow tiny on-background-alt" style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap">
            {{ art.denomination }}
          </span>
          <span class="tiny on-disabled shrink-0" style="min-width: 90px; text-align: right">
            {{ art.quantite | number:'1.0-2' }} {{ art.unite }}
          </span>
          <span class="tiny on-disabled shrink-0" style="min-width: 70px; text-align: right">{{ art.total | number:'1.0-0' }} €</span>
          <span class="tiny on-accent shrink-0" style="min-width: 45px; text-align: right">{{ art.percentage }}%</span>
        </div>

        @if (isOpen(parentKey + '::' + art.denomination)) {
          <!-- LEVEL 3 : Lots -->
          <div class="flex-col gap-2 mt-1 mb-2" style="margin-left: 28px">
            @for (lot of art.lots; track $index) {
              <div class="flex-row gap-8 p-4 align-center border-on-disabled border-box border-8">
                <div class="flex-col gap-2 grow" style="min-width: 0">
                  <span class="tiny on-background-alt" style="font-weight: 600">{{ lot.lotNumero }}</span>
                  <div class="flex-row gap-12 wrap">
                    @if (lot.dluo) {
                      <vl-date-limite-display [dluo]="lot.dluo" [typeDateLimite]="lot.typeDateLimite" size="compact"/>
                    }
                    <span class="tiny on-disabled">{{ lot.entrepot }}</span>
                  </div>
                </div>
                <div class="flex-col gap-2 shrink-0 align-end">
                  <span class="tiny on-disabled" style="min-width: 80px; text-align: right">{{ lot.quantite | number:'1.0-2' }} {{ lot.unite }}</span>
                  <span class="tiny on-disabled" style="min-width: 80px; text-align: right">{{ lot.coutUnitaire | number:'1.2-2' }} €/u</span>
                </div>
                <span class="tiny on-disabled shrink-0" style="min-width: 70px; text-align: right; font-weight: 600">{{ lot.total | number:'1.0-0' }} €</span>
              </div>
            }
          </div>
        }
      </div>
    </ng-template>
  </div>
</div>
